import {
  EmojiPickerTrigger,
  TextAreaMarkdown,
  TextInput,
} from '@hubtype/ui-react-web'
import { TextArea } from '@hubtype/ui-react-web'
import { MDXEditorMethods } from '@mdxeditor/editor'
import { memo, useEffect, useRef, useState } from 'react'

import { INPUT_DEBOUNCE_DELAY } from '../../../constants'
import { useDebounce } from '../../../custom-hooks'
import { useFlowBuilderSelector } from '../../../reducer/hooks'
import { InputAction } from '../constants'
import { EditorProps } from '../node-editor-panel'
import { BotVariablesSelect } from './bot-variables-select'

interface TextFieldProps extends EditorProps {
  id: string
  value: string
  changeProps?: {
    buttonIndex?: number
    elementIndex?: number
    sectionId?: string
    rowId?: string
    conditionValueId?: string
  }
  field: InputAction
  hasVariables?: boolean
  multiline?: boolean
  markdown?: boolean
  hasLists?: boolean
  hasLink?: boolean
  hasEmojiPicker?: boolean
  isInvalid?: boolean
  isOptional?: boolean
  maxLength?: number
  defaultRows?: number
  isLabelHidden?: boolean
  errorMessage?: string
  isCounterEnabled?: boolean
  footerRightSlot?: React.ReactNode
}

export const ContentTextField = memo((props: TextFieldProps): JSX.Element => {
  const currentLocale = useFlowBuilderSelector(ctx => ctx.state.currentLocale)
  const [value, setValue] = useState(props.value)
  const ref = useRef<HTMLInputElement>(null)
  const mdxEditorRef = useRef<MDXEditorMethods>(null)
  const debouncedValue = useDebounce<string>(value, INPUT_DEBOUNCE_DELAY)

  useEffect(() => {
    if (mdxEditorRef.current) {
      setTimeout(() => {
        mdxEditorRef.current?.setMarkdown(props.value)
      }, 0)
    }
    setValue(props.value)
  }, [props.id, currentLocale])

  useEffect(() => updateContent(), [debouncedValue])
  useEffect(() => setValue(props.value), [props.value])

  const updateContent = () => {
    if (value === props.value) return
    const sanitizedValue = removeZeroWidthSpaces(value)

    props.onChange({
      ...props.changeProps,
      type: props.field.actionType,
      fieldKey: props.field.key,
      value: sanitizedValue,
    })
  }

  const removeZeroWidthSpaces = (inputString: string) => {
    return inputString.replace(/\u200B/g, '')
  }

  const onUseVariable = (variable: string) => {
    if (mdxEditorRef.current) {
      mdxEditorRef.current.focus()
      mdxEditorRef.current.insertMarkdown(`{${variable}}`)
      mdxEditorRef.current.focus()
      return
    }
    const textWithVariable = addVariableOnText(variable)
    setValue(textWithVariable)
    ref.current?.focus()
  }

  const addVariableOnText = (variable: string): string => {
    const cursor = ref?.current?.selectionStart || props.value.length
    const textBeforeCursor = props.value.slice(0, cursor)
    const textAfterCursor = props.value.slice(cursor)
    return `${textBeforeCursor}{${variable}}${textAfterCursor}`
  }

  const isTextTooLong = props.maxLength ? value.length > props.maxLength : false
  const errorMessage = isTextTooLong
    ? 'Text is too long'
    : props.field.errorText || props.errorMessage

  const handleEmojiSelect = (emoji: string) => {
    if (mdxEditorRef.current) {
      mdxEditorRef.current.insertMarkdown(emoji)
      mdxEditorRef.current.focus()
    }
  }

  const Component = props.markdown
    ? TextAreaMarkdown
    : props.multiline
      ? TextArea
      : TextInput

  return (
    <Component
      {...props}
      //@ts-expect-error ref is not a valid prop for TextInput
      ref={ref}
      mdxEditorRef={mdxEditorRef}
      label={props.field.label}
      placeholder={props.field.placeholder}
      value={value}
      onChange={setValue}
      description={props.field.helperText}
      errorMessage={errorMessage}
      isInvalid={props.isInvalid || isTextTooLong}
      maxLength={props.maxLength}
      isReadOnly={props.isReadOnly}
      isMissing={!props.isOptional && props.showMissingFields && !value}
      footerLeftSlot={
        props.hasVariables && (
          <BotVariablesSelect
            isReadOnly={props.isReadOnly}
            onUseVariable={onUseVariable}
          />
        )
      }
      footerRightSlot={
        <>
          {props.footerRightSlot}
          {props.hasEmojiPicker && !props.isReadOnly && (
            <EmojiPickerTrigger
              inputRef={ref}
              value={value}
              onChange={mdxEditorRef.current ? handleEmojiSelect : setValue}
              isDisabled={props.isReadOnly}
            />
          )}
        </>
      }
    />
  )
})
